import { getApiUrl } from '../config/configService';

export interface ReleaseNotes {
  type?: string;
  items?: string[];
}

export interface VersionInfo {
  version: string;
  displayVersion: string;
  major: number;
  minor: number;
  build: string;
  buildDate?: string;
  commit?: string;
  shortCommit?: string;
  releaseNotes?: ReleaseNotes | null;
}

export interface PackageVersionInfo {
  publishedAtUtc: string | null;
}

export interface VersionsResponse {
  ui: VersionInfo | null;
  api: VersionInfo | null;
  package: PackageVersionInfo | null;
}

export const unavailableVersionLabel = 'Unavailable';

export const getVersionLabel = (
  versionInfo: VersionInfo | null | undefined,
): string => versionInfo?.version ?? unavailableVersionLabel;

/**
 * Fetches UI version from the static version.json file
 */
export const getUIVersion = async (): Promise<VersionInfo> => {
  try {
    const response = await fetch('/version.json');
    if (!response.ok) {
      throw new Error(`Failed to load UI version: ${response.status}`);
    }
    const data = await response.json();
    return {
      version: data.version || '0.0.0-unknown',
      displayVersion: data.displayVersion || 'unknown',
      major: data.major || 0,
      minor: data.minor || 0,
      build: data.build || '',
      buildDate: data.buildDate,
      commit: data.commit,
      shortCommit: data.shortCommit,
      releaseNotes: data.releaseNotes || null,
    };
  } catch (error) {
    console.error('Error fetching UI version:', error);
    throw error;
  }
};

export const getUiVersionLabel = async (): Promise<string> => {
  try {
    const versionInfo = await getUIVersion();
    return getVersionLabel(versionInfo);
  } catch (error) {
    console.error('Error resolving UI version label:', error);
    return unavailableVersionLabel;
  }
};

/**
 * Fetches API version from the API endpoint
 */
export const getAPIVersion = async (): Promise<VersionInfo | null> => {
  try {
    const apiUrl = await getApiUrl();
    const response = await fetch(`${apiUrl}version`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load API version: ${response.status}`);
    }

    const data = await response.json();
    return {
      version: data.version || '0.0.0-unknown',
      displayVersion: data.displayVersion || 'unknown',
      major: data.major || 0,
      minor: data.minor || 0,
      build: data.build || '',
      buildDate: data.buildDate,
      commit: data.commit,
      shortCommit: data.shortCommit,
      releaseNotes: data.releaseNotes || null,
    };
  } catch (error) {
    console.error('Error fetching API version:', error);
    // Return null instead of throwing to allow UI to still display
    return null;
  }
};

/**
 * Fetches Package version from the API endpoint
 */
export const getPackageVersion = async (): Promise<PackageVersionInfo | null> => {
  try {
    const apiUrl = await getApiUrl();
    const response = await fetch(`${apiUrl}Packages/usedPackage/publishDate`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load Package version: ${response.status}`);
    }

    const data = await response.json();
    return {
      publishedAtUtc: data.publishedAtUtc || null,
    };
  } catch (error) {
    console.error('Error fetching Package version:', error);
    // Return null instead of throwing to allow UI to still display
    return null;
  }
};

/**
 * Fetches both UI and API versions
 * Uses Promise.allSettled to ensure one failure doesn't prevent the other from loading
 */
export const getVersions = async (): Promise<VersionsResponse> => {
  const [uiResult, apiResult, packageResult] = await Promise.allSettled([
    getUIVersion(),
    getAPIVersion(),
    getPackageVersion()
  ]);

  return {
    ui: uiResult.status === 'fulfilled' ? uiResult.value : null,
    api: apiResult.status === 'fulfilled' && apiResult.value !== null 
      ? apiResult.value 
      : null,
    package: packageResult.status === 'fulfilled' && packageResult.value !== null
      ? packageResult.value
      : null,
  };
};

